import React, { useEffect, useState, useCallback } from 'react'
import {
  Box,
  Typography,
  styled,
  Select,
  TextField,
  FormHelperText,
} from '@mui/material'
import type { AdminSettingViewModel } from '@rsmus/ecp-userservice'
import { useForm, Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import FormAdminSettingCard from 'src/rsmCoreComponents/components/DynamicForm/FormControls/FormAdminSettingCard'
import {
  ADMIN_SETTINGS_POWER_BI_REPORTS_KEY,
  ADMIN_SETTINGS_POWER_BI_REPORT_INSTANCES_KEY,
} from 'src/utils/constants/constants'
import CustomErrorAlert from '../../components/forms/Alert/CustomErrorAlert'
import CustomSuccessAlert from '../../components/forms/Alert/CustomSuccessAlert'
import api from '../../api'
import AdminSettingsPowerBIReport from '../../components/AdminSettings/AdminSettingsPowerBIReport'
import AdminSettingsPowerBIReportInstance from '../../components/AdminSettings/AdminSettingsPowerBIReportInstance'

const StyledTextField = styled(TextField)({
  '.MuiInputBase-root': { width: '100%', maxWidth: '100%' },
  '& .MuiFormHelperText-root': { margin: 0 },
})

const StyledSelectField = styled(Select)({
  '.MuiOutlinedInput-root': { width: '100%', maxWidth: '100%' },
  '.MuiSelect-select': { fontFamily: 'Prelo-Book' },
})

const StyledLabel = styled('label')(({ theme }) => ({
  fontFamily: 'Prelo-Black, sans-serif',
  fontSize: '1rem',
  paddingBottom: '0.5rem',
  color: theme.palette.text.primary,
  display: 'block',
}))

const AdminSettingsForm = () => {
  const { t } = useTranslation()
  const {
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
    watch,
    clearErrors,
    resetField,
  } = useForm()

  const [settings, setSettings] = useState<AdminSettingViewModel[]>([])
  const [selectedKey, setSelectedKey] = useState<string>('')
  const [open, setOpen] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [isShowAddPowerBIReport, setShowAddPowerBIReport] = useState(false)
  const [isShowAddPowerBIReportInstance, setShowAddPowerBIReportInstance] =
    useState(false)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const isSpotlight = selectedKey === 'SpotlightArticleUrl'
  const secondaryText = watch('SecondaryText') || ''

  const fetchSettings = async () => {
    const res = await api.user.adminSettings_GetAll()
    setSettings(res?.data || [])
  }

  const fetchSettingDetails = async (key: string) => {
    try {
      const res = await api.user.adminSettings_GetByKey(key)
      if (!res?.data || typeof res.data !== 'object') return

      const setting = res.data
      const keyLower = setting.key?.toLowerCase() ?? ''

      // Handle Power BI Reports key
      if (keyLower === ADMIN_SETTINGS_POWER_BI_REPORTS_KEY) {
        setShowAddPowerBIReport(true)
        setShowAddPowerBIReportInstance(false)
      } else if (keyLower === ADMIN_SETTINGS_POWER_BI_REPORT_INSTANCES_KEY) {
        // Handle Power BI Report Instances key
        setShowAddPowerBIReport(false)
        setShowAddPowerBIReportInstance(true)
      } else {
        setShowAddPowerBIReport(false)
        setShowAddPowerBIReportInstance(false)
      }

      setValue('DisplayName', setting.displayName || '')
      setValue('SampleValue', setting.sampleValue || '')
      setValue('Description', setting.description || '')
      setValue('Value', setting.value || '')
      setValue('SecondaryText', setting.secondaryText || '')
    } catch {
      // Error fetching setting details - form values remain empty
    }
  }

  useEffect(() => {
    fetchSettings()
  }, [])

  interface SettingsFormData {
    Key?: string
    DisplayName?: string
    SampleValue?: string
    Description?: string
    Value?: string
    SecondaryText?: string
  }

  const onSubmit = async (data: SettingsFormData) => {
    const keyLower = selectedKey?.toLowerCase()
    if (
      keyLower === ADMIN_SETTINGS_POWER_BI_REPORTS_KEY ||
      keyLower === ADMIN_SETTINGS_POWER_BI_REPORT_INSTANCES_KEY
    ) {
      // Power BI settings are handled separately by their own components
      return
    }
    try {
      const payload = {
        value: data.Value ?? '',
        secondaryText: data.SecondaryText ?? '',
      } as unknown as AdminSettingViewModel

      await api.user.adminSettings_UpdateSetting(selectedKey, payload)
      setIsSuccess(true)
      setErrorMessage('')
      await fetchSettingDetails(selectedKey)

      reset({
        Key: '',
        DisplayName: '',
        SampleValue: '',
        Description: '',
        Value: '',
        SecondaryText: '',
      })
      setSelectedKey('')
    } catch (err: unknown) {
      setIsSuccess(false)
      await fetchSettingDetails(selectedKey)
      const errorObj = err as { data?: { error?: { exceptionMessage?: string }; message?: string } }
      const backendMessage =
        errorObj?.data?.error?.exceptionMessage ||
        errorObj?.data?.message ||
        'Update failed. Try again.'
      setErrorMessage(backendMessage)
    }
    setOpen(true)
  }

  const handleClose = useCallback(() => setOpen(false), [])

  /**
   * Renders the appropriate content section based on the selected setting key.
   * Extracted to avoid nested ternary expressions.
   */
  const renderSettingsContent = () => {
    if (isShowAddPowerBIReport) {
      return <AdminSettingsPowerBIReport />
    }
    if (isShowAddPowerBIReportInstance) {
      return <AdminSettingsPowerBIReportInstance />
    }
    return (
      <>
        {['DisplayName', 'SampleValue', 'Description'].map((fieldName) => (
          <Box key={fieldName} sx={{ marginBottom: '1.5rem' }}>
            <StyledLabel htmlFor={fieldName}>
              {t(`AdminSettingsForm.Labels.${fieldName}`)}
            </StyledLabel>
            <Controller
              name={fieldName}
              control={control}
              render={({ field }) => (
                <StyledTextField
                  id={fieldName}
                  value={field.value}
                  onChange={field.onChange}
                  disabled
                  fullWidth
                  multiline={fieldName === 'Description'}
                  rows={fieldName === 'Description' ? 3 : 1}
                />
              )}
            />
          </Box>
        ))}

        {isSpotlight && (
          <Box sx={{ marginBottom: '1.5rem' }}>
            <StyledLabel htmlFor="SecondaryText">
              {t('AdminSettingsForm.Labels.SecondaryText')}{' '}
              <Typography
                component="span"
                sx={{ ml: 1, fontWeight: 400, color: 'text.secondary' }}>
                ({secondaryText.length}/250 {t('characters', 'characters')})
              </Typography>
            </StyledLabel>
            <Controller
              name="SecondaryText"
              control={control}
              rules={{
                maxLength: { value: 250, message: 'Max 250 characters' },
              }}
              render={({ field }) => (
                <StyledTextField
                  id="SecondaryText"
                  value={field.value}
                  onChange={field.onChange}
                  inputProps={{ maxLength: 250 }}
                  fullWidth
                />
              )}
            />
            {!!errors.SecondaryText && (
              <FormHelperText error>
                {(errors.SecondaryText.message as string) || ''}
              </FormHelperText>
            )}
          </Box>
        )}

        {/* Editable Value */}
        <Box sx={{ marginBottom: '2rem' }}>
          <StyledLabel htmlFor="Value">
            {t(`AdminSettingsForm.Labels.Value`)} *
          </StyledLabel>
          <Controller
            name="Value"
            control={control}
            rules={
              isSpotlight
                ? undefined
                : { required: t('AdminSettingsForm.Errors.ValueRequired') }
            }
            render={({ field }) => (
              <StyledTextField
                id="Value"
                value={field.value}
                onChange={field.onChange}
                multiline
                rows={4}
                fullWidth
                error={!!errors.Value}
                helperText={(errors.Value?.message as string) || ''}
              />
            )}
          />
        </Box>
      </>
    )
  }

  return (
    <FormAdminSettingCard
      title={t('AdminSettingsForm.Title')}
      handleSubmit={handleSubmit(onSubmit)}
      isSubmitDisabled={!selectedKey}
      hideSubmit={isShowAddPowerBIReport || isShowAddPowerBIReportInstance}>
      {isSuccess && (
        <CustomSuccessAlert
          open={open}
          close={handleClose}
          header={t('AdminSettingsForm.Success.Title')}
          message={t('AdminSettingsForm.Success.Message')}
          testId="AdminSetting_Success"
          disableAutoHide
        />
      )}
      {!isSuccess && !!errorMessage && (
        <CustomErrorAlert
          open={open}
          close={handleClose}
          header="Error"
          message={errorMessage}
          testId="AdminSetting_Error"
          disableAutoHide
        />
      )}

      <Box sx={{ marginBottom: '1.5rem' }}>
        <StyledLabel htmlFor="Key">
          {t('AdminSettingsForm.Labels.Key')} *
        </StyledLabel>
        <Controller
          name="Key"
          control={control}
          defaultValue={selectedKey}
          rules={{ required: t('AdminSettingsForm.Errors.RequiredField') }}
          render={({ field }) => (
            <StyledSelectField
              native
              id="Key"
              value={field.value}
              onChange={(e) => {
                const key = e.target.value as string
                setSelectedKey(key)
                field.onChange(key)
                clearErrors()
                resetField('Value')
                resetField('SecondaryText')

                if (!key) {
                  setValue('DisplayName', '')
                  setValue('DotNetType', '')
                  setValue('Description', '')
                  setValue('Value', '')
                  setValue('SecondaryText', '')
                  setShowAddPowerBIReport(false)
                  setShowAddPowerBIReportInstance(false)
                  return
                }

                fetchSettingDetails(key)
              }}
              inputProps={{ 'aria-describedby': 'KeyError' }}
              error={!!errors.Key}>
              <option value="">Select Key</option>
              {settings.map((s) => (
                <option key={s.key} value={s.key}>
                  {s.key}
                </option>
              ))}
            </StyledSelectField>
          )}
        />
        {!!errors.Key && (
          <FormHelperText error id="KeyError">
            {(errors.Key.message as string) || ''}
          </FormHelperText>
        )}
      </Box>

      {renderSettingsContent()}
    </FormAdminSettingCard>
  )
}

export default AdminSettingsForm
